/** Primary business phone — use everywhere we show or dial the number */
export const COMPANY_PHONE = '9619583336';
export const COMPANY_PHONE_TEL = `tel:${COMPANY_PHONE}`;
/** Same number for WhatsApp (India country code + local mobile, no spaces) */
export const COMPANY_WHATSAPP_URL = `https://wa.me/91${COMPANY_PHONE}`;

/** Official email addresses */
export const COMPANY_EMAIL_DOCUMENT = 'document@omsaifin.com';
export const COMPANY_EMAIL_ENQUIRY = 'enquiry@omsaifin.com';
/** Shown on Contact page (document first for formal correspondence) */
export const COMPANY_EMAIL_LINES = [COMPANY_EMAIL_DOCUMENT, COMPANY_EMAIL_ENQUIRY];

/** Registered office — use everywhere we show the address */
export const COMPANY_ADDRESS_LINES = [
  '3rd Floor, Vidhata Apartment',
  'Shankar Raghoba Road, Near Datta Mandir',
  'Thane West 400601',
];
export const COMPANY_ADDRESS_INLINE = COMPANY_ADDRESS_LINES.join(', ');

/** Design tokens — enterprise BFSI: restrained, high clarity */
export const brand = {
  primary: '#0f766e',
  primaryDark: '#115e59',
  primaryLight: '#14b8a6',
  accent: '#b45309',
  accentDark: '#92400e',
  indigo: '#3730a3',
  slate: '#0f172a',
  muted: '#475569',
  surface: '#f1f5f9',
  surfaceWarm: '#f8fafc',
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  heroGradient: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)',
  ctaGradient: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)',
  faqBand: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
  homeHeroBg: 'linear-gradient(165deg, #0f172a 0%, #1e293b 38%, #134e4a 72%, #0f766e 100%)',
  homeGlass: 'rgba(15, 23, 42, 0.42)',
  homeGlassBorder: 'rgba(255, 255, 255, 0.14)',
  homeInk: '#f8fafc',
  homeInkMuted: 'rgba(248, 250, 252, 0.78)',
  sectionSlate: '#1e293b',
  sectionSlateDeep: '#0f172a',
  /** Headings — deep slate (less green overall) */
  forest: '#0f172a',
  forestDark: '#020617',
  /** Solution cards — indigo / amber / teal mix */
  solutionCard1: '#3730a3',
  solutionCard2: '#c2410c',
  solutionCard3: '#0f766e',
  /** Compact section accents */
  stepAccent1: '#4f46e5',
  stepAccent2: '#0f766e',
  stepAccent3: '#b45309',
  /** Pill CTA on hero — light teal, readable on photos */
  heroCtaPill: '#ccfbf1',
  heroCtaPillText: '#042f2e',
};

/** Loan / finance photography (Unsplash) — replace with your own assets in /public anytime */
export const loanImages = {
  hero: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=2400&q=85&auto=format&fit=crop',
  testimonialBand:
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=2400&q=80&auto=format&fit=crop',
  stepConsult: 'https://images.unsplash.com/photo-1454165803956-220721a6a9d2?w=1200&q=80&auto=format&fit=crop',
  stepDocs: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=1200&q=80&auto=format&fit=crop',
  stepClose: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&q=80&auto=format&fit=crop',
};

/** Full-width page heroes — one image per route, matched to page topic */
export const pageHeroImages = {
  home: loanImages.hero,
  about:
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=2400&q=80&auto=format&fit=crop',
  services:
    'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=2400&q=80&auto=format&fit=crop',
  insurance:
    'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=2400&q=80&auto=format&fit=crop',
  contact:
    'https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=2400&q=80&auto=format&fit=crop',
  emiCalculator:
    'https://images.unsplash.com/photo-1554224154-22dec7ec8818?w=2400&q=80&auto=format&fit=crop',
  testimonials:
    'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=2400&q=80&auto=format&fit=crop',
};

const PAGE_HERO_OVERLAY = `linear-gradient(115deg, rgba(15, 23, 42, 0.9) 0%, rgba(49, 46, 129, 0.55) 42%, rgba(15, 118, 110, 0.35) 100%)`;

/** Inner pages — same as About: Services, Contact, Insurance, EMI, Testimonials */
export const unifiedHeroMinHeight = {
  xs: 'min(78svh, 720px)',
  sm: 'min(72svh, 800px)',
  md: 'min(68svh, 920px)',
};

/** Home page only — extra tall hero */
export const unifiedHeroMinHeightHome = {
  xs: 'min(96svh, 880px)',
  sm: 'min(92svh, 960px)',
  md: 'min(88svh, 1180px)',
};

/** Contact page — taller hero than other inner pages */
export const contactHeroMinHeight = {
  xs: 'min(94svh, 900px)',
  sm: 'min(90svh, 980px)',
  md: 'min(86svh, 1080px)',
};

/** Services page — tall hero band */
export const servicesHeroMinHeight = {
  xs: 'min(92svh, 860px)',
  sm: 'min(88svh, 940px)',
  md: 'min(84svh, 1040px)',
};

/** Shared inner-hero type (read comfortably, not “small UI”) */
export const pageHeroType = {
  eyebrow: {
    color: 'rgba(255,255,255,0.88)',
    fontWeight: 700,
    letterSpacing: '0.22em',
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    display: 'block',
  },
  title: {
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-0.03em',
    textShadow: '0 2px 28px rgba(0,0,0,0.35)',
    fontSize: { xs: '2.35rem', sm: '2.85rem', md: '3.35rem' },
    lineHeight: 1.08,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.92)',
    fontWeight: 500,
    lineHeight: 1.75,
    fontSize: { xs: '1.125rem', md: '1.2rem' },
    maxWidth: 720,
  },
};

export const pageHeroSx = {
  minHeight: { xs: 'min(520px, calc(100dvh - 96px))', md: 480 },
  display: 'flex',
  alignItems: 'center',
  py: { xs: 4, md: 3 },
  boxSizing: 'border-box',
};

export const pageHeroFlushSx = {
  ...pageHeroSx,
  pt: 0,
  pb: { xs: 4, md: 5 },
  minHeight: { xs: 'auto', md: 360 },
};

export const homeHeroSx = {
  minHeight: { xs: 'min(560px, calc(100dvh - 72px))', md: 520 },
  display: 'flex',
  alignItems: 'center',
  pt: { xs: 3, md: 4 },
  pb: { xs: 7, md: 9 },
  px: 0,
  mt: 0,
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
};

/** Inner pages — photo + gradient band (same min-height as About everywhere) */
export function pageHeroRichSxFor(imageUrl) {
  return {
    position: 'relative',
    overflow: 'hidden',
    minHeight: unifiedHeroMinHeight,
    display: 'flex',
    alignItems: 'center',
    py: { xs: 10, md: 14 },
    px: { xs: 2, sm: 3 },
    boxSizing: 'border-box',
    backgroundImage: `${PAGE_HERO_OVERLAY}, url(${imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
  };
}

/** @deprecated Prefer pageHeroRichSxFor(pageHeroImages.<route>) for distinct art */
export const pageHeroRichSx = pageHeroRichSxFor(loanImages.hero);

export const pageElevatedCardSx = {
  borderRadius: '20px',
  border: `1px solid ${brand.border}`,
  bgcolor: '#fff',
  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
};
